import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Table as TableIcon,
  LayoutGrid,
  Search,
  X,
  Download,
  Calendar as CalendarIcon,
  BarChart3,
  Filter as FilterIcon,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { OperationsTable } from './OperationsTable';
import { OperationsCards } from './OperationsCards';
import { CalendarView } from './CalendarView';
import { ChartView } from './ChartView';
import { ConditionalFormatDialog } from './ConditionalFormatDialog';
import { useNetSuiteFreshness } from './netsuiteFreshness';
import { SavedViewPicker } from './SavedViewPicker';
import { DisplayOptions } from './DisplayOptions';
import { ActiveFiltersBar } from './ActiveFiltersBar';
import { processRows } from './processRows';
import { GroupMode, OperationsViewState, useSavedViews } from './savedViews';
import {
  CalendarConfig,
  ChartConfig,
  ColumnDef,
  ColumnId,
  ConditionalFormatRule,
  CustomColumnDef,
  DateFilter,
  DateFilterState,
  DisplayOptions as DisplayOptionsState,
  FilterState,
  FormatsState,
  SortState,
  Stage,
  TotalsState,
  ViewMode,
} from './types';
import { setFieldApi } from '@/lib/setFieldApi';
import { useAuth } from '@/contexts/AuthContext';
import { compileFormula, coerceNumber, FormulaValue } from './formulaEngine';

const DEFAULT_DISPLAY: DisplayOptionsState = {
  rowSpacing: 'tight',
  zebra: false,
  hiddenColumns: new Set(),
};

function defaultHiddenColumns<T>(columns: ColumnDef<T>[]): Set<string> {
  return new Set(columns.filter((c) => c.defaultHidden).map((c) => c.id));
}

function applyColumnOrder<T>(
  columns: ColumnDef<T>[],
  order: ColumnId[] | undefined
): ColumnDef<T>[] {
  if (!order || order.length === 0) return columns;
  const byId = new Map(columns.map((c) => [c.id, c]));
  const out: ColumnDef<T>[] = [];
  const seen = new Set<string>();
  for (const id of order) {
    const c = byId.get(id);
    if (c && !seen.has(id)) {
      out.push(c);
      seen.add(id);
    }
  }
  for (const c of columns) {
    if (!seen.has(c.id)) out.push(c);
  }
  return out;
}

function defaultFiltersFromColumns<T>(columns: ColumnDef<T>[]): FilterState {
  const out: FilterState = {};
  for (const c of columns) {
    if (c.defaultFilter && c.defaultFilter.length > 0) {
      out[c.id] = new Set(c.defaultFilter);
    }
  }
  return out;
}

interface OperationsViewProps<T extends { id: string }> {
  columns: ColumnDef<T>[];
  useData: () => T[];
  savedViewsEntityKey: string;
  defaultSort?: SortState;
  titleColumnId?: string;
  stageAccessor?: (row: T) => Stage | undefined;
  entityLabel?: string;
  showSavedViewPicker?: boolean;
  initialViewId?: string;
  onRowClick?: (row: T) => void;
  /** Show a "Updated Xm ago / Refreshing…" indicator (live NetSuite views only). */
  showDataFreshness?: boolean;
}

export function OperationsView<T extends { id: string }>({
  columns,
  useData,
  savedViewsEntityKey,
  defaultSort,
  titleColumnId,
  stageAccessor,
  entityLabel = 'records',
  showSavedViewPicker = false,
  initialViewId,
  onRowClick,
  showDataFreshness = false,
}: OperationsViewProps<T>) {
  const allRows = useData();
  const { views, loading: viewsLoading, createView, updateView, deleteView, currentUserId } =
    useSavedViews(savedViewsEntityKey);

  const DEFAULT_STATE: OperationsViewState = useMemo(
    () => ({
      sort: defaultSort ?? [],
      filters: defaultFiltersFromColumns(columns),
      dateFilters: {},
      totals: new Set(),
      formats: {},
      viewMode: 'table',
      display: {
        ...DEFAULT_DISPLAY,
        hiddenColumns: defaultHiddenColumns(columns),
      },
      groupBy: undefined,
      groupMode: 'detail',
      calendarConfig: undefined,
      chartConfig: undefined,
      columnOrder: undefined,
      columnLabels: undefined,
      columnWidths: undefined,
      pinnedFilterColumns: undefined,
      customColumns: undefined,
    }),
    [defaultSort, columns]
  );

  const [viewMode, setViewMode] = useState<ViewMode>(DEFAULT_STATE.viewMode);
  const [sort, setSort] = useState<SortState>(DEFAULT_STATE.sort);
  const [filters, setFilters] = useState<FilterState>(() => defaultFiltersFromColumns(columns));
  const [dateFilters, setDateFilters] = useState<DateFilterState>({});
  const [totals, setTotals] = useState<TotalsState>(DEFAULT_STATE.totals);
  const [formats, setFormats] = useState<FormatsState>(DEFAULT_STATE.formats);
  const [display, setDisplay] = useState<DisplayOptionsState>(() => ({
    ...DEFAULT_DISPLAY,
    hiddenColumns: defaultHiddenColumns(columns),
  }));
  const [groupBy, setGroupBy] = useState<ColumnId | undefined>(undefined);
  const [groupMode, setGroupMode] = useState<GroupMode>('detail');
  const [calendarConfig, setCalendarConfig] = useState<CalendarConfig | undefined>(undefined);
  const [chartConfig, setChartConfig] = useState<ChartConfig | undefined>(undefined);
  const [columnOrder, setColumnOrder] = useState<ColumnId[] | undefined>(undefined);
  const [columnLabels, setColumnLabels] = useState<Record<ColumnId, string> | undefined>(undefined);
  const [columnWidths, setColumnWidths] = useState<Record<ColumnId, number> | undefined>(undefined);
  const [pinnedFilterColumns, setPinnedFilterColumns] = useState<Set<ColumnId>>(() => new Set());
  const [customColumns, setCustomColumns] = useState<CustomColumnDef[]>([]);
  // Open state is session-local (not persisted to view) — matches SharePoint.
  const [filterRowOpen, setFilterRowOpen] = useState(false);

  const { token } = useAuth();

  const handleActionClick = async (row: T, def: import('./types').ActionColumnDef) => {
    try {
      await setFieldApi(token, {
        id: row.id,
        field: def.targetField,
        value: def.setValue,
      });
      // Backend broadcasts an updated snapshot over the socket, so we don't
      // need to manually patch state here — the polling/socket service is
      // the source of truth.
    } catch (err) {
      console.error(`Action "${def.label}" failed`, err);
    }
  };

  // Synthetic ColumnDef instances for the user's custom action columns. They
  // merge into the column list so reorder / hide / column-header-menu all
  // work uniformly; the `render` short-circuits to a button so the cell never
  // shows an accessor value.
  const actionColumnsAsDefs: ColumnDef<T>[] = useMemo(() => {
    return customColumns
      .filter((c): c is import('./types').ActionColumnDef => c.type === 'action')
      .map((def) => ({
        id: def.id,
        label: def.label,
        accessor: () => '',
        sortValue: () => '',
        totalType: 'count',
        category: 'basic',
        render: (row: T) => (
          <ActionButton
            label={def.buttonLabel}
            onClick={() => handleActionClick(row, def)}
          />
        ),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customColumns, token]);

  // Synthetic ColumnDefs for formula columns. Each expression is compiled once;
  // the accessor/sort/numeric callbacks evaluate it per row against a resolver
  // that maps `[Field]` references onto the registry columns' values. Formula
  // columns reference only registry columns (not other custom columns) to avoid
  // evaluation cycles.
  const formulaColumnsAsDefs: ColumnDef<T>[] = useMemo(() => {
    const colByName = new Map<string, ColumnDef<T>>();
    const colById = new Map<string, ColumnDef<T>>();
    columns.forEach((c) => {
      colByName.set(c.label.toLowerCase(), c);
      colById.set(c.id, c);
    });

    return customColumns
      .filter((c): c is import('./types').FormulaColumnDef => c.type === 'formula')
      .map((def) => {
        const compiled = compileFormula(def.expression);
        const resolverFor = (row: T) => (name: string): FormulaValue => {
          const col = colByName.get(name.toLowerCase()) ?? colById.get(name);
          if (!col) return null;
          if ((col.dataType === 'date' || col.dataType === 'number') && col.numericValue) {
            return col.numericValue(row);
          }
          return col.accessor(row);
        };
        const evalRow = (row: T): FormulaValue =>
          compiled.ok ? compiled.evaluate(resolverFor(row)) : null;

        const formatNumber = (n: number): string => {
          if (!isFinite(n)) return '';
          if (typeof def.decimals === 'number') return n.toFixed(def.decimals);
          return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(4)));
        };

        const isNumber = def.resultType === 'number';

        const synthetic: ColumnDef<T> = {
          id: def.id,
          label: def.label,
          category: 'basic',
          dataType: isNumber ? 'number' : 'string',
          totalType: isNumber ? 'sum' : 'count',
          accessor: (row: T) => {
            if (!compiled.ok) return '#ERR';
            const v = evalRow(row);
            if (v == null) return '';
            return isNumber ? formatNumber(coerceNumber(v)) : String(v);
          },
          sortValue: (row: T) =>
            isNumber ? coerceNumber(evalRow(row)) : String(evalRow(row) ?? ''),
        };
        if (isNumber) synthetic.numericValue = (row: T) => coerceNumber(evalRow(row));
        return synthetic;
      });
  }, [customColumns, columns]);

  // Reorder + relabel columns ONCE; everything downstream (table, headers,
  // gear, chart axes, calendar pills, etc.) sees the user-arranged version.
  const effectiveColumns = useMemo(() => {
    const all = [...columns, ...actionColumnsAsDefs, ...formulaColumnsAsDefs];
    const ordered = applyColumnOrder(all, columnOrder);
    if (!columnLabels) return ordered;
    return ordered.map((c) =>
      columnLabels[c.id] ? { ...c, label: columnLabels[c.id] } : c
    );
  }, [columns, actionColumnsAsDefs, formulaColumnsAsDefs, columnOrder, columnLabels]);
  const [formatColumn, setFormatColumn] = useState<ColumnId | null>(null);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const currentState: OperationsViewState = {
    viewMode,
    sort,
    filters,
    dateFilters,
    totals,
    formats,
    display,
    groupBy,
    groupMode,
    calendarConfig,
    chartConfig,
    columnOrder,
    columnLabels,
    columnWidths,
    pinnedFilterColumns: pinnedFilterColumns.size > 0 ? Array.from(pinnedFilterColumns) : undefined,
    customColumns: customColumns.length > 0 ? customColumns : undefined,
  };

  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const dirty = activeView
    ? !statesEqual(currentState, activeView.state)
    : !statesEqual(currentState, DEFAULT_STATE);

  const applyState = (s: OperationsViewState) => {
    setViewMode(s.viewMode);
    setSort(s.sort);
    setFilters(s.filters);
    setDateFilters(s.dateFilters ?? {});
    setTotals(new Set(s.totals));
    setFormats(s.formats);
    setDisplay({
      rowSpacing: s.display.rowSpacing,
      zebra: s.display.zebra,
      hiddenColumns: new Set(s.display.hiddenColumns),
    });
    setGroupBy(s.groupBy);
    setGroupMode(s.groupMode ?? 'detail');
    setCalendarConfig(s.calendarConfig);
    setChartConfig(s.chartConfig);
    setColumnOrder(s.columnOrder);
    setColumnLabels(s.columnLabels);
    setColumnWidths(s.columnWidths);
    setPinnedFilterColumns(new Set(s.pinnedFilterColumns ?? []));
    setCustomColumns(s.customColumns ?? []);
  };

  const handleSelectView = (id: string) => {
    const v = views.find((x) => x.id === id);
    if (!v) return;
    setActiveViewId(id);
    applyState(v.state);
  };

  const handleCreateView = async (name: string, isShared: boolean) => {
    try {
      const view = await createView(name, currentState, isShared);
      setActiveViewId(view.id);
    } catch (err) {
      console.error('Failed to create view', err);
    }
  };

  const handleUpdateView = async (id: string) => {
    try {
      await updateView(id, { state: currentState });
    } catch (err) {
      console.error('Failed to update view', err);
    }
  };

  const handleDeleteView = async (id: string) => {
    try {
      await deleteView(id);
      if (activeViewId === id) setActiveViewId(null);
    } catch (err) {
      console.error('Failed to delete view', err);
    }
  };

  const handleClearView = () => {
    // "Clear" falls back to the system Default for this entity if one exists,
    // otherwise to the column-registry defaults. Either way, the user gets the
    // out-of-the-box starting state, not an arbitrary previous view.
    const sysDefault = views.find((v) => v.isSystem && v.name === 'Default');
    if (sysDefault) {
      setActiveViewId(sysDefault.id);
      applyState(sysDefault.state);
    } else {
      setActiveViewId(null);
      applyState(DEFAULT_STATE);
    }
  };

  // On first mount: apply the explicit `initialViewId` if supplied (deep-link
  // from Saved Views page), else auto-apply the system "Default" view for this
  // entity, else fall through to the column-registry default state.
  const appliedInitialRef = useRef(false);
  useEffect(() => {
    if (appliedInitialRef.current) return;
    if (viewsLoading) return;

    let target = null;
    if (initialViewId) {
      target = views.find((v) => v.id === initialViewId) ?? null;
    }
    if (!target) {
      target = views.find((v) => v.isSystem && v.name === 'Default') ?? null;
    }
    appliedInitialRef.current = true;
    if (target) {
      setActiveViewId(target.id);
      applyState(target.state);
    }
  }, [initialViewId, views, viewsLoading]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((row) => columns.some((col) => col.accessor(row).toLowerCase().includes(q)));
  }, [allRows, search, columns]);

  const processedRows = useMemo(
    () => processRows(searchedRows, columns, filters, sort, dateFilters),
    [searchedRows, columns, filters, sort, dateFilters]
  );

  const handleSort = (
    columnId: ColumnId,
    action: 'replace-asc' | 'replace-desc' | 'append-asc' | 'append-desc' | 'remove'
  ) => {
    setSort((prev) => {
      switch (action) {
        case 'replace-asc':
          return [{ columnId, direction: 'asc' }];
        case 'replace-desc':
          return [{ columnId, direction: 'desc' }];
        case 'append-asc':
          return [...prev.filter((s) => s.columnId !== columnId), { columnId, direction: 'asc' }];
        case 'append-desc':
          return [...prev.filter((s) => s.columnId !== columnId), { columnId, direction: 'desc' }];
        case 'remove':
          return prev.filter((s) => s.columnId !== columnId);
      }
    });
  };

  const handleFilterChange = (columnId: ColumnId, values: Set<string>) =>
    setFilters((prev) => ({ ...prev, [columnId]: values }));

  const handleDateFilterChange = (columnId: ColumnId, df: DateFilter | null) =>
    setDateFilters((prev) => {
      const next = { ...prev };
      if (df) next[columnId] = df;
      else delete next[columnId];
      return next;
    });

  const handleToggleTotal = (columnId: ColumnId) =>
    setTotals((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });

  const handleApplyFormat = (columnId: ColumnId, rules: ConditionalFormatRule[]) =>
    setFormats((prev) => {
      const next = { ...prev };
      if (rules.length === 0) delete next[columnId];
      else next[columnId] = rules;
      return next;
    });

  const handleRemoveFormat = (columnId: ColumnId) =>
    setFormats((prev) => {
      const next = { ...prev };
      delete next[columnId];
      return next;
    });

  const handleTogglePinFilter = (columnId: ColumnId) =>
    setPinnedFilterColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });

  const activeFilterCount = useMemo(() => {
    let n = 0;
    for (const v of Object.values(filters)) if (v && v.size > 0) n++;
    for (const v of Object.values(dateFilters)) if (v) n++;
    return n;
  }, [filters, dateFilters]);
  const hasAnyFilter = activeFilterCount > 0;

  const handleClearAllFilters = () => {
    setFilters({});
    setDateFilters({});
  };

  const handleAddCustomColumn = (def: CustomColumnDef) =>
    setCustomColumns((prev) => [...prev, def]);

  const handleRemoveCustomColumn = (id: ColumnId) =>
    setCustomColumns((prev) => prev.filter((c) => c.id !== id));

  const handleClearColumnFilter = (colId: ColumnId) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
    setDateFilters((prev) => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
  };

  const columnById = useMemo(() => {
    const m = new Map<string, ColumnDef<T>>();
    for (const c of columns) m.set(c.id, c);
    return m;
  }, [columns]);

  // Title-case the entity label for the page heading (entityLabel is lowercase
  // plural: "work orders" → "Work Orders").
  const pageTitle = entityLabel.replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-3 space-y-0">
      <h1 className="text-[24px] font-semibold text-stone-900 leading-tight mb-2">
        {pageTitle}
      </h1>
      <div className="flex items-center gap-0.5 pb-1.5 mb-2 border-b border-stone-200">
        {/* LEFT: global search */}
        <SearchBox value={search} onChange={setSearch} entityLabel={entityLabel} />

        {/* RIGHT: view selector, layout switcher, icon-only action buttons */}
        <div className="ml-auto flex items-center gap-0.5">
          {showDataFreshness && <DataFreshness />}
          {showSavedViewPicker && (
            <SavedViewPicker
              views={views}
              activeViewId={activeViewId}
              currentState={currentState}
              currentUserId={currentUserId}
              dirty={dirty}
              onSelect={handleSelectView}
              onCreate={handleCreateView}
              onUpdate={handleUpdateView}
              onDelete={handleDeleteView}
              onClear={handleClearView}
            />
          )}
          {viewMode === 'table' && (
            <FilterPaneToggle
              open={filterRowOpen}
              hasActiveFilters={hasAnyFilter}
              activeFilterCount={activeFilterCount}
              onClick={() => setFilterRowOpen((v) => !v)}
            />
          )}
          <div className="inline-flex items-center bg-stone-100/70 rounded-sm p-0.5 h-8 mx-0.5">
            <ViewToggleButton
              active={viewMode === 'table'}
              onClick={() => setViewMode('table')}
              icon={<TableIcon className="w-4 h-4" />}
              label="Table view"
            />
            <ViewToggleButton
              active={viewMode === 'cards'}
              onClick={() => setViewMode('cards')}
              icon={<LayoutGrid className="w-4 h-4" />}
              label="Card view"
            />
            <ViewToggleButton
              active={viewMode === 'calendar'}
              onClick={() => setViewMode('calendar')}
              icon={<CalendarIcon className="w-4 h-4" />}
              label="Calendar view"
            />
            <ViewToggleButton
              active={viewMode === 'chart'}
              onClick={() => setViewMode('chart')}
              icon={<BarChart3 className="w-4 h-4" />}
              label="Chart view"
            />
          </div>
          <DisplayOptions
            value={display}
            columns={effectiveColumns}
            viewMode={viewMode}
            calendarConfig={calendarConfig}
            chartConfig={chartConfig}
            columnOrder={columnOrder}
            filters={filters}
            dateFilters={dateFilters}
            sort={sort}
            formats={formats}
            groupBy={groupBy}
            customColumns={customColumns}
            onChange={setDisplay}
            onCalendarChange={setCalendarConfig}
            onChartChange={setChartConfig}
            onReorder={setColumnOrder}
            onAddCustomColumn={handleAddCustomColumn}
            onRemoveCustomColumn={handleRemoveCustomColumn}
          />
          <button
            onClick={() => exportCsv(processedRows, effectiveColumns, display.hiddenColumns, entityLabel)}
            className="flex items-center justify-center w-8 h-8 rounded-sm text-stone-700 hover:bg-stone-100 transition-colors"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {viewMode === 'table' && filterRowOpen && (
        <ActiveFiltersBar
          columns={effectiveColumns}
          filters={filters}
          dateFilters={dateFilters}
          onClearColumn={handleClearColumnFilter}
          onClearAll={handleClearAllFilters}
        />
      )}
      {viewMode === 'table' && (
        <OperationsTable
              rows={processedRows}
              allRows={searchedRows}
              columns={effectiveColumns}
              sort={sort}
              filters={filters}
              dateFilters={dateFilters}
              totals={totals}
              formats={formats}
              display={display}
              groupBy={groupBy}
              groupMode={groupMode}
              onSort={handleSort}
              onFilterChange={handleFilterChange}
              onDateFilterChange={handleDateFilterChange}
              onToggleTotal={handleToggleTotal}
              onOpenFormat={(colId) => setFormatColumn(colId)}
              onSetGroupBy={(colId) => setGroupBy(colId)}
              onSetGroupMode={setGroupMode}
              onRenameColumn={(colId, label) =>
                setColumnLabels((prev) => {
                  const next = { ...(prev ?? {}) };
                  if (label) next[colId] = label;
                  else delete next[colId];
                  return Object.keys(next).length === 0 ? undefined : next;
                })
              }
              columnWidths={columnWidths}
              onResizeColumn={(colId, width) =>
                setColumnWidths((prev) => {
                  const next = { ...(prev ?? {}) };
                  if (width === undefined) delete next[colId];
                  else next[colId] = width;
                  return Object.keys(next).length === 0 ? undefined : next;
                })
              }
          pinnedFilterColumns={pinnedFilterColumns}
          onTogglePinFilter={handleTogglePinFilter}
          onRowClick={onRowClick}
        />
      )}
      {viewMode === 'cards' && (
        <OperationsCards
          rows={processedRows}
          columns={effectiveColumns}
          formats={formats}
          display={display}
          titleColumnId={titleColumnId}
          stageAccessor={stageAccessor}
        />
      )}
      {viewMode === 'calendar' && (
        <CalendarView
          rows={processedRows}
          columns={effectiveColumns}
          visibleColumns={effectiveColumns.filter((c) => !display.hiddenColumns.has(c.id))}
          config={calendarConfig}
          titleColumnId={titleColumnId}
          onRowClick={onRowClick}
        />
      )}
      {viewMode === 'chart' && (
        <ChartView
          rows={processedRows}
          columns={effectiveColumns}
          formats={formats}
          config={chartConfig}
        />
      )}

      <ConditionalFormatDialog
        open={formatColumn !== null}
        column={formatColumn ? columnById.get(formatColumn) ?? null : null}
        uniqueValues={
          formatColumn && columnById.get(formatColumn)
            ? Array.from(
                new Set(searchedRows.map((r) => columnById.get(formatColumn)!.accessor(r)))
              )
                .filter(Boolean)
                .sort()
            : []
        }
        existingRules={formatColumn ? formats[formatColumn] : undefined}
        onApply={(rules) => formatColumn && handleApplyFormat(formatColumn, rules)}
        onRemove={() => formatColumn && handleRemoveFormat(formatColumn)}
        onOpenChange={(o) => {
          if (!o) setFormatColumn(null);
        }}
      />
    </div>
  );
}

function statesEqual(a: OperationsViewState, b: OperationsViewState): boolean {
  if (a.viewMode !== b.viewMode) return false;
  if (JSON.stringify(a.sort) !== JSON.stringify(b.sort)) return false;
  if (JSON.stringify(a.formats) !== JSON.stringify(b.formats)) return false;

  const aTotals = Array.from(a.totals).sort();
  const bTotals = Array.from(b.totals).sort();
  if (aTotals.length !== bTotals.length || aTotals.some((v, i) => v !== bTotals[i])) return false;

  const aFilterKeys = Object.keys(a.filters).filter((k) => (a.filters[k]?.size ?? 0) > 0);
  const bFilterKeys = Object.keys(b.filters).filter((k) => (b.filters[k]?.size ?? 0) > 0);
  if (aFilterKeys.length !== bFilterKeys.length) return false;
  for (const k of aFilterKeys) {
    const av = Array.from(a.filters[k] ?? []).sort();
    const bv = Array.from(b.filters[k] ?? []).sort();
    if (av.length !== bv.length || av.some((v, i) => v !== bv[i])) return false;
  }

  const aDate = a.dateFilters ?? {};
  const bDate = b.dateFilters ?? {};
  const aDateKeys = Object.keys(aDate).filter((k) => !!aDate[k]);
  const bDateKeys = Object.keys(bDate).filter((k) => !!bDate[k]);
  if (aDateKeys.length !== bDateKeys.length) return false;
  for (const k of aDateKeys) {
    if (JSON.stringify(aDate[k]) !== JSON.stringify(bDate[k])) return false;
  }

  if (a.display.rowSpacing !== b.display.rowSpacing) return false;
  if (a.display.zebra !== b.display.zebra) return false;
  const aHidden = Array.from(a.display.hiddenColumns).sort();
  const bHidden = Array.from(b.display.hiddenColumns).sort();
  if (aHidden.length !== bHidden.length || aHidden.some((v, i) => v !== bHidden[i])) return false;

  if ((a.groupBy ?? '') !== (b.groupBy ?? '')) return false;
  if ((a.groupMode ?? 'detail') !== (b.groupMode ?? 'detail')) return false;
  if (JSON.stringify(a.calendarConfig ?? null) !== JSON.stringify(b.calendarConfig ?? null))
    return false;
  if (JSON.stringify(a.chartConfig ?? null) !== JSON.stringify(b.chartConfig ?? null)) return false;
  if (JSON.stringify(a.columnOrder ?? null) !== JSON.stringify(b.columnOrder ?? null)) return false;
  if (JSON.stringify(a.columnLabels ?? null) !== JSON.stringify(b.columnLabels ?? null))
    return false;
  if (JSON.stringify(a.columnWidths ?? null) !== JSON.stringify(b.columnWidths ?? null))
    return false;

  const aPinned = [...(a.pinnedFilterColumns ?? [])].sort();
  const bPinned = [...(b.pinnedFilterColumns ?? [])].sort();
  if (aPinned.length !== bPinned.length || aPinned.some((v, i) => v !== bPinned[i]))
    return false;

  if (JSON.stringify(a.customColumns ?? null) !== JSON.stringify(b.customColumns ?? null))
    return false;

  return true;
}

interface ActionButtonProps {
  label: string;
  onClick: () => void | Promise<void>;
}

function ActionButton({ label, onClick }: ActionButtonProps) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      disabled={busy}
      onClick={async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
          await onClick();
        } finally {
          setBusy(false);
        }
      }}
      className={cn(
        'inline-flex items-center justify-center h-6 px-2.5 rounded-sm text-xs font-medium transition-colors',
        busy
          ? 'bg-stone-100 text-stone-400 cursor-wait'
          : 'bg-[#0078d4] text-white hover:bg-[#106ebe]'
      )}
    >
      {label}
    </button>
  );
}

function exportCsv<T>(
  rows: T[],
  columns: ColumnDef<T>[],
  hiddenColumns: Set<string>,
  entityLabel: string
) {
  const cols = columns.filter((c) => !hiddenColumns.has(c.id));
  const escape = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const header = cols.map((c) => escape(c.label)).join(',');
  const body = rows.map((r) => cols.map((c) => escape(c.accessor(r))).join(',')).join('\n');
  const csv = `${header}\n${body}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${entityLabel}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function relativeTime(ts: number): string {
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}

/**
 * Live freshness indicator for the NetSuite-backed view. Shows "Refreshing…"
 * while a background fetch is in flight, otherwise "Updated Xm ago" off the
 * last data arrival. Driven by the shared store in useOperationsData.
 */
function DataFreshness() {
  const { lastUpdatedAt, refreshing } = useNetSuiteFreshness();
  const [, tick] = useState(0);
  // Re-render every 15s so the relative time stays current.
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);

  if (!lastUpdatedAt && !refreshing) return null;

  return (
    <span
      className="hidden sm:flex items-center gap-1 px-1.5 text-[11px] text-stone-400 select-none"
      title={lastUpdatedAt ? `Last updated ${new Date(lastUpdatedAt).toLocaleTimeString()}` : undefined}
    >
      <RefreshCw className={cn('w-3 h-3', refreshing && 'animate-spin text-[#0078d4]')} />
      {refreshing ? 'Refreshing…' : `Updated ${relativeTime(lastUpdatedAt)}`}
    </span>
  );
}

interface SearchBoxProps {
  value: string;
  onChange: (v: string) => void;
  entityLabel: string;
}

function SearchBox({ value, onChange, entityLabel }: SearchBoxProps) {
  return (
    <div className="w-72 flex items-center gap-2 bg-stone-100/60 hover:bg-stone-100 focus-within:bg-white focus-within:ring-1 focus-within:ring-[#0078d4] rounded-sm h-8 pl-2.5 pr-1 transition-colors">
      <Search className="w-4 h-4 text-stone-500 shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Search ${entityLabel}`}
        className="flex-1 min-w-0 text-[13px] outline-none bg-transparent placeholder:text-stone-500"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="text-stone-400 hover:text-stone-700 p-1 shrink-0"
          title="Clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

interface ViewToggleButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

interface FilterPaneToggleProps {
  open: boolean;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  onClick: () => void;
}

function FilterPaneToggle({
  open,
  hasActiveFilters,
  activeFilterCount,
  onClick,
}: FilterPaneToggleProps) {
  // Visual is "tinted" whenever filters are active OR the row is expanded —
  // the user should know filters are applied even when the row is collapsed.
  const tinted = open || hasActiveFilters;
  return (
    <button
      onClick={onClick}
      aria-pressed={open}
      title={open ? 'Hide active filters' : 'Show active filters'}
      className={cn(
        'flex items-center justify-center w-8 h-8 rounded-sm transition-colors relative',
        tinted
          ? 'bg-[#0078d4]/10 text-[#0078d4]'
          : 'text-stone-700 hover:bg-stone-100'
      )}
    >
      <FilterIcon className="w-4 h-4" />
      {activeFilterCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-1 rounded-full text-[9px] font-bold flex items-center justify-center bg-[#0078d4] text-white">
          {activeFilterCount}
        </span>
      )}
    </button>
  );
}

function ViewToggleButton({ active, onClick, icon, label }: ViewToggleButtonProps) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'flex items-center justify-center w-8 h-7 rounded-sm transition-colors',
        active
          ? 'bg-[#0078d4] text-white'
          : 'text-stone-600 hover:text-stone-900 hover:bg-white'
      )}
    >
      {icon}
    </button>
  );
}

